import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SurveyRoutes from './SurveyRoutes';
import ErrorBoundary from 'react/shared/components/ErrorBoundary';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import api from 'react/signups/card/api.js';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import { goToNextStep, shortenRecipe, lengthenRecipe } from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import recipeShape from 'react/signups/card/shapes/Recipe';
import { yesNoToBoolean, booleanToYesNo, potentialTrustee } from 'react/signups/card/utils';

export default function Survey({
  cardAccountSignup,
  recipe,
  setCardAccountSignup,
  setMdWidthCard,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // State hooks ------------------------------------------------

  const [sendingData, setSendingData] = useState(false);

  // module navigation state
  const [localRecipe, setLocalRecipe] = useState(recipe);
  const [moduleComplete, setModuleComplete] = useState(false);

  // form data state
  const [userTypeData, setUserTypeData] = useState({
    userType: cardAccountSignup.userType || '',
  });

  // form data state
  const [emailData, setEmailData] = useState(cardAccountSignup.email || '');

  const [cardForData, setCardForData] = useState({
    firstName: cardAccountSignup.cardholderFirstName || '',
    lastName: cardAccountSignup.cardholderLastName || '',
    cardFor: cardAccountSignup.cardFor || [],
    cardForOther: cardAccountSignup.cardForOther || '',
    cardForPaidCaregiver: cardAccountSignup.cardForPaidCaregiver || '',
    cardForTeenOrChild: '', //preserve the value of cardForTeenOrChild, but don't save it to the DB
  });

  const [rolesData, setRolesData] = useState({
    roles: cardAccountSignup.roles || [],
    rolesOther: cardAccountSignup.rolesOther || '',
  });

  const [trusteeData, setTrusteeData] = useState({
    trustee: booleanToYesNo(cardAccountSignup.trustee),
  });

  const [trustDetails, setTrustDetails] = useState({
    trustName: cardAccountSignup.trustName || '',
    isTrustee: cardAccountSignup.trustName ? 'yes' : '',
  });

  const [thanksData, setThanksData] = useState(false);

  // Effect hooks and memoized callbacks ------------------------

  // Move to next step or mark module as complete
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      recipe: localRecipe,
      moduleComplete,
      setModuleComplete,
      userType: userTypeData.userType,
    });
  }, [
    cardAccountSignup.id,
    location,
    navigate,
    localRecipe,
    moduleComplete,
    setModuleComplete,
    userTypeData,
  ]);

  // Set the width of the top level container
  useUpdateEffect(() => {
    const { userType } = userTypeData;
    if (trusteeData.trustee === 'yes') {
      setMdWidthCard(false);
    } else {
      setMdWidthCard(true);
    }
    if (userType === USER_TYPES.FAMILY) {
      if (trusteeData.trustee === 'yes') {
        // include trustName step in family navigation sequence
        setLocalRecipe(
          lengthenRecipe({
            addAfterPath: '/survey/trustee',
            addPaths: ['/survey/trust_details'],
            recipe: localRecipe,
            userType,
          }),
        );
      } else {
        // remove trustName step from family navigation sequence
        setLocalRecipe(
          shortenRecipe({
            skipPaths: ['/survey/trust_details'],
            recipe: localRecipe,
            userType,
          }),
        );
      }
    } else {
      // reset to trigger state change
      setLocalRecipe({ ...localRecipe });
    }
  }, [trusteeData]);

  // Send module payload and update Application-level state
  useEffect(() => {
    if (!moduleComplete || sendingData) return;
    const params = {
      card_account_signup: {
        cardFor: cardForData.cardFor,
        cardForOther: cardForData.cardForOther,
        cardForPaidCaregiver: cardForData.cardForPaidCaregiver,
        cardholderFirstName: cardForData.firstName,
        cardholderLastName: cardForData.lastName,
        roles: rolesData.roles,
        rolesOther: rolesData.rolesOther,
        trustee: yesNoToBoolean(trusteeData.trustee),
        trustName: trustDetails.trustName,
        userType: userTypeData.userType,
      },
    };

    setSendingData(true);
    api
      .createSignup(params)
      .then((response) => {
        const cardAccountSignup = response.data.data;
        const { attributes, id, relationships } = cardAccountSignup;
        setCardAccountSignup({ id, relationships, ...attributes, email: emailData });
        onNavigate();
      })
      .catch(() => {
        setSendingData(false);
        window.Truelink.flash('error', 'There was an error saving Card Account Signup');
        setModuleComplete(false);
      });
  }, [
    moduleComplete,
    cardForData,
    rolesData,
    setCardAccountSignup,
    trusteeData,
    trustDetails,
    userTypeData,
    sendingData,
    onNavigate,
    emailData,
  ]);

  useEffect(() => {
    // if the user flow includes a trustee, then `setMdWidthCard` should be false so it has a smaller width
    // if the user is a family member and the card is for a child or recovery (and the trustee is no), set the width to true
    if (
      cardForData.cardFor.length <= 2 &&
      trusteeData.trustee !== 'yes' &&
      (cardForData.cardFor.includes('recovery') || cardForData.cardFor.includes('teen-or-child'))
    ) {
      setMdWidthCard(true);
    }
  }, [cardForData, setMdWidthCard, trusteeData]);

  // rebuild local recipe when Module-level state changes in response to form submit
  useUpdateEffect(() => {
    const { userType } = userTypeData;

    if (userType === USER_TYPES.FAMILY) {
      if (!potentialTrustee(cardForData.cardFor)) {
        // remove trustee step from family navigation sequence
        setLocalRecipe(
          shortenRecipe({
            skipPaths: ['/survey/trustee', '/survey/trust_details'],
            recipe: localRecipe,
            userType,
          }),
        );
      } else {
        // include trustee step in family navigation sequence
        setLocalRecipe(
          lengthenRecipe({
            addAfterPath: '/survey/card_for',
            addPaths: ['/survey/trustee'],
            recipe: localRecipe,
            userType,
          }),
        );
      }
    } else {
      // reset to trigger state change
      setLocalRecipe({ ...localRecipe });
    }
  }, [cardForData]);

  // navigate to next step when Module-level state changes in response to form submit or recipe rebuild
  useUpdateEffect(() => {
    if (sendingData) {
      return;
    }
    onNavigate();
  }, [userTypeData, rolesData, trustDetails, thanksData, localRecipe]);

  return (
    <ErrorBoundary>
      <SurveyRoutes
        defaultPath={recipe.defaultPath}
        passThroughParams={{
          cardholderFirstName: cardForData.firstName,
          cardFor: cardForData.cardFor,
          cardForData,
          emailData,
          rolesData,
          trusteeData,
          trustDetails,
          userTypeData,
          moduleComplete,
          setCardAccountSignup,
          setEmailData,
          setUserTypeData,
          setRolesData,
          setCardForData,
          setTrustDetails,
          setTrusteeData,
          setThanksData,
        }}
        userType={userTypeData.userType}
      />
    </ErrorBoundary>
  );
}

Survey.propTypes = {
  cardAccountSignup: cardAccountSignupShape.isRequired,
  recipe: recipeShape.isRequired,
  setCardAccountSignup: PropTypes.func.isRequired,
  setMdWidthCard: PropTypes.func,
};
